import React, { useState } from 'react'
import { components, MenuListProps } from 'react-select'
import {
  type TenantCommodity,
  useTenantCommodities,
} from 'src/api/queries/microservices/trade-commodity-config'
import { HStack, InfoOutlineIcon, Select, Text } from 'src/ui'
import { type SelectOption } from 'src/ui/select-utils'
import { useDebounce } from 'src/utils'
import { DEFAULT_PAGE_SIZE } from 'src/utils/microservice/paginated-query'

type TenantCommodityPickerProps = {
  tenantId: string
  value: TenantCommodity | null
  isDisabled: boolean
  placeholder: string
  id: string
  onChange: (commodity: TenantCommodity | null) => void
}

type CommodityOption = SelectOption & {
  commodity: TenantCommodity
}

const commodityOption = (commodity: TenantCommodity): CommodityOption => ({
  value: commodity.id,
  label: `${commodity.displayName} (${commodity.sourceId})`,
  commodity,
})

const commodityOptions = (commodities: TenantCommodity[]): CommodityOption[] =>
  commodities.map(commodityOption)

const SearchHintMenuList = (props: MenuListProps) => (
  <components.MenuList {...props}>
    {props.children}
    <HStack color="gray.600" fontSize="sm" px="10px" py="6px" spacing={2}>
      <InfoOutlineIcon flexShrink={0} />
      <Text>Search to narrow results and view more</Text>
    </HStack>
  </components.MenuList>
)

const searchHintComponents = { MenuList: SearchHintMenuList }

export default function TenantCommodityPicker({
  tenantId,
  value,
  isDisabled,
  placeholder,
  id,
  onChange,
}: TenantCommodityPickerProps) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const commoditiesQuery = useTenantCommodities({ tenantId, name: debouncedSearch })
  const options = commodityOptions(commoditiesQuery.data ?? [])
  const isSearching = search !== debouncedSearch || commoditiesQuery.isFetching

  return (
    <Select
      id={id}
      value={value ? commodityOption(value) : null}
      width="100%"
      options={options}
      components={
        (commoditiesQuery.data?.length ?? 0) >= DEFAULT_PAGE_SIZE && !isSearching
          ? searchHintComponents
          : undefined
      }
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={isSearching}
      filterOption={() => !isSearching}
      onChange={(option: CommodityOption | null) => onChange(option?.commodity ?? null)}
      onInputChange={(inputValue, actionMeta) => {
        if (actionMeta.action === 'input-change' || inputValue === '') setSearch(inputValue)
        return inputValue
      }}
    />
  )
}
